/**
 * Wave 29 Chunk 2 — Org-wide expiring-documents dashboard (HR-only).
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, AlertTriangle, Calendar, FileText } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function DocumentsExpiring() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isHr = user && ["owner", "admin"].includes(user.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    if (user && !isHr) nav("/", { replace: true });
  }, [user, isHr, nav]);

  useEffect(() => {
    if (!isHr) return;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/team/documents/expiring?days=${days}`);
        setItems(data.items || []);
      } catch (_e) { /* noop */ }
      finally { setLoading(false); }
    })();
  }, [isHr, days]);

  if (!isHr) return null;
  return (
    <div className="space-y-4" data-testid="docs-expiring-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/team" className="hv-btn hv-btn-ghost text-xs" data-testid="exp-back">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to Team
        </Link>
        <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 inline-flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> Expiring documents
        </div>
        <span className="w-12" />
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-[#a0aabf]">Within</span>
        {[7, 14, 30, 90].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            className={`text-[11px] px-2.5 py-1 rounded-full border ${
              days === d ? "bg-[#1FB6C1] text-[#0a0b0e] border-[#1FB6C1]" : "border-white/15 text-[#a0aabf]"
            }`}
            data-testid={`exp-days-${d}`}
          >
            {d} days
          </button>
        ))}
      </div>
      {loading ? (
        <div className="h-20 bg-white/[0.04] rounded animate-pulse" />
      ) : items.length === 0 ? (
        <div className="hv-card p-8 text-center text-sm text-[#647087]" data-testid="exp-empty">
          🎉 Nothing expires in the next {days} days.
        </div>
      ) : (
        <div className="space-y-1" data-testid="exp-list">
          {items.map((d) => (
            <Link key={d.id} to={`/team/${d.user_id}`} className="hv-card p-3 flex items-center gap-3 hover:border-amber-400/40">
              <FileText className="w-4 h-4 text-amber-300" />
              <div className="flex-1">
                <div className="text-sm text-white">{d.employee_name}</div>
                <div className="text-[11px] text-[#a0aabf]">{d.label || d.kind} · expires {new Date(d.expiry_date).toLocaleDateString("en-ZA")}</div>
              </div>
              <Calendar className="w-3 h-3 text-[#647087]" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
